import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Typography } from "antd";

const { Text } = Typography;

/**
 * 手写签名面板：从主单"点击签名"入口弹出的放大书写区（约 520px 宽的居中 Modal + 长方形画布，宽高比约 3.3:1，与打印签名区一致，签名横向铺开、出图无留白好压缩），
 * 比主单上的入口大得多，便于手写。自行实现 canvas 手写（无额外依赖）：处理高分屏
 * devicePixelRatio 缩放、笔画中点二次曲线平滑、书写时用 touch-action:none 禁页面滚动；
 * pointercancel（手势被系统打断）按抬笔处理。点"确认"输出 PNG dataURL 交给上层持久化。
 * 已知限制：打开后中途转屏 / 改变窗口尺寸不会重排画布（重排会清空已写笔迹），签到一半建议不要转屏。
 */
export default function SignaturePad({ open, onCancel, onConfirm }) {
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const drawingRef = useRef(false);
    const lastRef = useRef({ x: 0, y: 0 });
    const emptyRef = useRef(true);
    const [canConfirm, setCanConfirm] = useState(false);

    const setupCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        // 用 clientWidth/Height（CSS 布局尺寸），不受 Modal 打开动画 transform: scale 影响。
        // 若改用 getBoundingClientRect，会在动画首帧拿到被缩小的尺寸，画布坐标系随之变小，
        // 之后落笔位置超出画布、看起来像"画不出来"（鼠标尤其明显）。
        const cssW = canvas.clientWidth;
        const cssH = canvas.clientHeight;
        if (!cssW || !cssH) return;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.round(cssW * dpr);
        canvas.height = Math.round(cssH * dpr);
        const ctx = canvas.getContext("2d");
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr); // 之后用 CSS 像素坐标绘制，高分屏不发虚
        ctx.lineWidth = 2.5;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = "#111";
        ctxRef.current = ctx;
        emptyRef.current = true;
        setCanConfirm(false);
    };

    useEffect(() => {
        if (!open) return undefined;
        const id = requestAnimationFrame(setupCanvas);
        return () => cancelAnimationFrame(id);
    }, [open]);

    const posOf = (event) => {
        const rect = canvasRef.current.getBoundingClientRect();
        return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    const handlePointerDown = (event) => {
        const ctx = ctxRef.current;
        if (!ctx) return;
        canvasRef.current.setPointerCapture?.(event.pointerId);
        drawingRef.current = true;
        const point = posOf(event);
        lastRef.current = point;
        ctx.beginPath();
        ctx.moveTo(point.x, point.y);
        ctx.lineTo(point.x + 0.1, point.y + 0.1); // 轻点一下也能落点
        ctx.stroke();
        emptyRef.current = false;
        setCanConfirm(true);
        event.preventDefault();
    };

    const handlePointerMove = (event) => {
        if (!drawingRef.current) return;
        const ctx = ctxRef.current;
        const point = posOf(event);
        const last = lastRef.current;
        const midX = (last.x + point.x) / 2;
        const midY = (last.y + point.y) / 2;
        ctx.quadraticCurveTo(last.x, last.y, midX, midY); // 中点二次曲线，去掉折线感
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(midX, midY);
        lastRef.current = point;
        event.preventDefault();
    };

    const handlePointerUp = () => {
        drawingRef.current = false;
    };

    const handleClear = () => {
        const canvas = canvasRef.current;
        const ctx = ctxRef.current;
        if (!canvas || !ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        emptyRef.current = true;
        setCanConfirm(false);
    };

    const handleConfirm = () => {
        if (emptyRef.current || !canvasRef.current) return;
        onConfirm(canvasRef.current.toDataURL("image/png"));
    };

    return (
        <Modal
            open={open}
            title="手写签名"
            onCancel={onCancel}
            afterOpenChange={(opened) => opened && setupCanvas()}
            destroyOnClose
            maskClosable={false}
            width="min(520px, 92vw)"
            footer={[
                <Button key="clear" onClick={handleClear}>
                    清除
                </Button>,
                <Button key="cancel" onClick={onCancel}>
                    取消
                </Button>,
                <Button key="confirm" type="primary" disabled={!canConfirm} onClick={handleConfirm}>
                    确认
                </Button>,
            ]}
        >
            <Text type="secondary">请在框内手写签名（手指 / 触控笔 / 鼠标）</Text>
            <canvas
                ref={canvasRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
                onPointerCancel={handlePointerUp}
                style={{
                    width: "100%",
                    aspectRatio: "3.3 / 1",
                    border: "1px solid #333",
                    borderRadius: 6,
                    touchAction: "none",
                    display: "block",
                    marginTop: 8,
                }}
            />
        </Modal>
    );
}
